import {Card, CardBody} from "@heroui/react";
import {Spinner} from "@heroui/spinner";
import {addToast} from "@heroui/toast";
import {Icon} from "@iconify/react";
import {useQuery, useQueryClient} from "@tanstack/react-query";
import {createFileRoute, useParams} from '@tanstack/react-router'
import {useMemo, useState} from "react";
import Content from "../../../components/Content.tsx";
import PButton from "../../../components/PButton.tsx";
import PInput from "../../../components/PInput.tsx";
import Header from "../../../Header.tsx";
import services from "../../../services";

export const Route = createFileRoute('/_app/running/races/$race_uid')({
    component: RouteComponent,
})

// Race-morning screen: find a runner fast, mark them in, add walk-ups. Built to be usable on a
// phone with cold hands, so the check-in target is a big row rather than a small button.
function RouteComponent() {
    const {race_uid} = useParams({strict: false}) as {race_uid: string};
    const queryClient = useQueryClient();
    const [search, setSearch] = useState('');
    const [showAdd, setShowAdd] = useState(false);
    const [newRunner, setNewRunner] = useState({name: '', email: '', bib: ''});
    const [busyUid, setBusyUid] = useState<string | null>(null);

    const entriesQuery = useQuery({
        queryKey: ['race-entries', race_uid],
        queryFn:  () => services.races.entries(race_uid),
    });

    const refresh = () => queryClient.invalidateQueries({queryKey: ['race-entries', race_uid]});

    const race = entriesQuery.data?.race;
    const summary = entriesQuery.data?.summary;
    const entries = entriesQuery.data?.entries || [];

    const filtered = useMemo(() => {
        const q = search.trim().toLowerCase();
        if (!q) return entries;
        return entries.filter((e: any) =>
            (e.name || '').toLowerCase().includes(q)
            || (e.bib || '').toLowerCase().includes(q)
            || (e.email || '').toLowerCase().includes(q));
    }, [entries, search]);

    async function setStatus(entry: any, status: string) {
        setBusyUid(entry.uid);
        try {
            await services.races.updateEntry(race_uid, entry.uid, {status});
            refresh();
        } catch (e: any) {
            addToast({color: 'danger', title: e?.message || 'Could not update that runner.'});
        }
        setBusyUid(null);
    }

    async function addRunner() {
        if (!newRunner.name.trim()) return;
        try {
            await services.races.addEntry(race_uid, newRunner);
            setNewRunner({name: '', email: '', bib: ''});
            setShowAdd(false);
            refresh();
            addToast({color: 'success', title: 'Runner added.'});
        } catch (e: any) {
            addToast({color: 'danger', title: e?.message || 'Could not add that runner.'});
        }
    }

    return <>
        <Header backUrl={"/running/races/"} />
        <Content>
            {entriesQuery.isLoading ? (
                <div className="flex justify-center py-12"><Spinner size="lg" /></div>
            ) : !race ? (
                <div className="py-12 text-center text-default-500">Race not found.</div>
            ) : (<>
                <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
                    <div>
                        <h1 className="text-2xl font-bold">{race.title}</h1>
                        <p className="text-sm text-default-500">Race day check-in</p>
                    </div>
                    <PButton color="primary" onPress={() => setShowAdd(!showAdd)}
                             startContent={<Icon icon="material-symbols-light:person-add-outline" className="text-xl" />}>
                        Add runner
                    </PButton>
                </div>

                <div className="mb-4 grid grid-cols-3 gap-3">
                    <div className="rounded-xl border border-default-200 bg-content1 p-3 text-center">
                        <div className="text-2xl font-bold">{summary?.checked_in ?? 0}</div>
                        <div className="text-xs uppercase text-default-500">Checked in</div>
                    </div>
                    <div className="rounded-xl border border-default-200 bg-content1 p-3 text-center">
                        <div className="text-2xl font-bold">{summary?.registered ?? 0}</div>
                        <div className="text-xs uppercase text-default-500">Still to arrive</div>
                    </div>
                    <div className="rounded-xl border border-default-200 bg-content1 p-3 text-center">
                        <div className="text-2xl font-bold">{summary?.total ?? 0}</div>
                        <div className="text-xs uppercase text-default-500">Entered</div>
                    </div>
                </div>

                {showAdd && (
                    <Card className="mb-4">
                        <CardBody className="space-y-3">
                            {/* Deliberately minimal, and not the sign-up form: this is for a runner
                                turning up on the day without an entry. Everyone else registers
                                through the normal event registration flow on the race's public page. */}
                            <p className="text-sm text-default-500">
                                For walk-ups on race day. Runners entering in advance sign up on the
                                race's public page, through the usual ScorePal registration flow.
                            </p>
                            <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                                <PInput label="Name" value={newRunner.name} onChange={(e) => setNewRunner({...newRunner, name: e.target.value})} />
                                <PInput label="Email (optional)" value={newRunner.email} onChange={(e) => setNewRunner({...newRunner, email: e.target.value})} />
                                <PInput label="Race number (optional)" value={newRunner.bib} onChange={(e) => setNewRunner({...newRunner, bib: e.target.value})} />
                            </div>
                            <div className="flex gap-2">
                                <PButton color="primary" isDisabled={!newRunner.name.trim()} onPress={addRunner}>Add</PButton>
                                <PButton variant="light" onPress={() => setShowAdd(false)}>Cancel</PButton>
                            </div>
                        </CardBody>
                    </Card>
                )}

                <PInput className="mb-3" label="" placeholder="Search name, race number or email…"
                        value={search} onChange={(e) => setSearch(e.target.value)}
                        startContent={<Icon icon="material-symbols-light:search" className="text-xl text-default-400" />} />

                {filtered.length === 0 ? (
                    <div className="py-10 text-center text-default-500">
                        {entries.length === 0 ? 'Nobody has entered yet.' : 'No runners match that search.'}
                    </div>
                ) : (
                    <div className="space-y-2">
                        {filtered.map((e: any) => {
                            const isIn = e.status === 'checked_in';
                            return (
                                <button key={e.uid}
                                        disabled={busyUid === e.uid}
                                        onClick={() => setStatus(e, isIn ? 'registered' : 'checked_in')}
                                        className={`flex w-full items-center justify-between rounded-xl border p-3 text-left transition ${
                                            isIn ? 'border-success bg-success-50 dark:bg-success-500/10' : 'border-default-200 bg-content1 hover:bg-default-50'
                                        }`}>
                                    <span className="min-w-0">
                                        <span className="block truncate font-semibold">{e.name}</span>
                                        <span className="block truncate text-xs text-default-500">
                                            {[e.bib ? `#${e.bib}` : null, e.category, e.club].filter(Boolean).join(' · ') || e.email || '—'}
                                        </span>
                                    </span>
                                    <span className={`shrink-0 rounded-full px-3 py-1 text-xs font-bold ${
                                        isIn ? 'bg-success text-white' : 'bg-default-100 text-default-600'
                                    }`}>
                                        {isIn ? '✓ Here' : 'Check in'}
                                    </span>
                                </button>
                            );
                        })}
                    </div>
                )}
            </>)}
        </Content>
    </>
}
